import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';

import { DEFAULT_LIST_COUNT } from '~/constants';
import { MarketplaceVendorsQuery } from '~/gql/graphql';
import { MarketplaceSort } from '~/types/marketplace';
import { getQueryString } from '~/utils/formatter';

export type useMarketplaceVendorsParams = {
  postcode?: string;
  categories?: Array<string>;
  promotions?: Array<string>;
  brands?: Array<string>;
  channel?: string | null;
  sort?: MarketplaceSort | null;
  leadId?: string | null;
  synthetic?: string | null;
  take?: number;
};

type useMarketplaceVendorsProps = {
  initialData?: MarketplaceVendorsQuery['marketplaceVendors'];
  params?: useMarketplaceVendorsParams;
  enabled?: boolean;
};

export const useMarketplaceVendors = ({ initialData, params, enabled = true }: useMarketplaceVendorsProps) => {
  const {
    leadId = null,
    postcode,
    categories,
    promotions,
    brands,
    channel = null,
    sort,
    synthetic = null,
    take,
  } = params || {};

  const queryKey = useMemo(() => {
    return [
      'vendors',
      {
        postcode,
        categories: categories?.join(','),
        promotions: promotions?.join(','),
        brands: brands?.join(','),
        channel,
        sort,
        take,
      },
    ];
  }, [brands, categories, channel, postcode, promotions, sort, take]);

  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, isSuccess, refetch } =
    useInfiniteQuery({
      queryKey,
      enabled: enabled && !initialData,
      queryFn: async ({ pageParam }) => {
        // when refetching pageParam was not reset
        const skip = pageParam ?? 0;
        const queryString = getQueryString([
          { name: 'leadId', value: leadId },
          { name: 'postcode', value: postcode },
          { name: 'categories', value: categories?.join(',') },
          { name: 'promotions', value: promotions?.join(',') },
          { name: 'manufacturerBrands', value: brands?.join(',') },
          { name: 'channel', value: channel },
          { name: 'sort', value: sort },
          { name: 'skip', value: skip },
          { name: 'synthetic', value: synthetic },
          { name: 'take', value: take },
        ]);

        const { data } = await axios.get<MarketplaceVendorsQuery['marketplaceVendors']>(
          `/api/marketplace/marketplace-vendors${queryString}`,
        );
        return data;
      },
      initialPageParam: 0,
      getNextPageParam: (_, pages) => {
        const runningCount = pages.reduce((acc, page) => acc + page.vendors.length, 0);
        const totalCount = pages[pages.length - 1].count || 0;

        if (runningCount >= totalCount) {
          return null;
        }

        return DEFAULT_LIST_COUNT * pages.length;
      },
      initialData: initialData
        ? {
            pageParams: [initialData.vendors?.length],
            pages: [initialData],
          }
        : undefined,
    });

  return {
    data: data?.pages.flatMap((page) => page) || [],
    error,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    status: isFetching ? 'loading' : isSuccess ? 'success' : 'error',
    fetchNextPage,
    refetch,
  };
};
